import { useEffect, useMemo, useState } from 'react';
import {
  getSurveySampleByIdApi,
  getSurveySampleDimensionsByIdApi,
} from '../../../api/survey.sample';
import { QUESTION_FILTER_TYPE, QUESTION_VALUE_TYPE } from '../../../constants/common';
import {
  getFilterSampleId,
  orderAttributeOptions,
  removeHiddenAttributeOptions,
} from '../../../helpers/filters';

const NO_SETTINGS = { ids: [], labels: [], order: [] };

// Reads what the survey wizard saved on a filter's sample: the hidden tags and
// the dragged tag order. Multi-select samples key both by dimension id; resolve
// those to the dimension labels as well, since the filter options may only
// carry labels.
const loadWizardSettings = async (sampleId, jwt) => {
  const sample = await getSurveySampleByIdApi(sampleId, jwt);
  const ids = (sample?.Data?.hiddenAnswers || []).map(String);
  const labelMode = [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(
    sample?.Data?.valueType
  );
  const orderIds = ((labelMode ? sample?.Data?.labelOrders : sample?.Data?.answerOrders) || []).map(
    String
  );
  if (ids.length === 0 && orderIds.length === 0) return NO_SETTINGS;
  if (!labelMode) return { ids, labels: [], order: orderIds.map((id) => ({ id })) };

  const dimensions = (await getSurveySampleDimensionsByIdApi(sampleId, jwt)) || [];
  const labelById = Object.fromEntries(
    dimensions.map((dimension) => [String(dimension.Id), dimension.Data.label])
  );
  const labels = ids.filter((id) => labelById[id] !== undefined).map((id) => labelById[id]);
  const order = orderIds.map((id) => ({ id, label: labelById[id] }));
  return { ids, labels, order };
};

// The filters endpoint does not apply the tag settings made in the survey
// wizard, so read each attribute filter's sample, strip its hidden options and
// sort the rest into the saved order before the filter and segment pickers see
// them.
const useVisibleFilters = (filters, jwt) => {
  const [settingsBySampleId, setSettingsBySampleId] = useState({});

  useEffect(() => {
    if (!filters || !jwt) return undefined;
    const sampleIds = [
      ...new Set(
        filters
          .filter((filter) => filter.type === QUESTION_FILTER_TYPE.ATTRIBUTE)
          .map(getFilterSampleId)
          .filter((sampleId) => sampleId !== undefined)
      ),
    ];
    if (sampleIds.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      sampleIds.map((sampleId) =>
        loadWizardSettings(sampleId, jwt)
          .then((settings) => [sampleId, settings])
          .catch((e) => {
            console.error(e);
            return [sampleId, NO_SETTINGS];
          })
      )
    ).then((entries) => {
      if (!cancelled) setSettingsBySampleId(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [filters, jwt]);

  return useMemo(() => {
    const orderBySampleId = Object.fromEntries(
      Object.entries(settingsBySampleId).map(([sampleId, settings]) => [sampleId, settings.order])
    );
    return orderAttributeOptions(
      removeHiddenAttributeOptions(filters, settingsBySampleId),
      orderBySampleId
    );
  }, [filters, settingsBySampleId]);
};

export default useVisibleFilters;
